/**
 * Enhanced Cart Management Script
 * Features:
 * 1. Group products by handle and combine quantities
 * 2. Vehicle fitment verification based on VFitz data
 * 3. Visual verification badges
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    // Selectors
    cartItemsSelector: '.cart-items tbody',
    cartItemSelector: '.cart-item',
    
    // Verification status classes
    verifiedClass: 'fitment-verified',
    notVerifiedClass: 'fitment-not-verified',
    universalClass: 'fitment-universal',
    
    // Messages
    messages: {
      verified: 'Fits Your Vehicle',
      notVerified: 'May Not Fit - Verify Compatibility',
      universal: 'Universal Fit',
      checking: 'Checking Fitment...'
    }
  };

  /**
   * Cart Item Manager
   * Handles grouping items by product handle
   */
  class CartManager {
    constructor() {
      this.cartItems = [];
      this.groupedItems = {};
    }

    /**
     * Initialize cart management
     */
    init() {
      this.collectCartItems();
      this.groupByHandle();
      this.displayGroupedItems();
      this.verifyFitment();
    }

    /**
     * Collect all cart items from DOM
     */
    collectCartItems() {
      const itemElements = document.querySelectorAll(CONFIG.cartItemSelector);
      
      itemElements.forEach(el => {
        const item = this.extractItemData(el);
        if (item) {
          this.cartItems.push(item);
        }
      });
    }

    /**
     * Extract item data from DOM element
     */
    extractItemData(element) {
      try {
        // Get product URL to extract handle
        const linkElement = element.querySelector('.cart-item__name');
        if (!linkElement) return null;
        
        const url = linkElement.getAttribute('href');
        const handle = this.extractHandleFromUrl(url);
        
        // Get quantity
        const qtyInput = element.querySelector('.quantity__input');
        const quantity = qtyInput ? parseInt(qtyInput.value) : 1;
        
        // Get product ID from element ID
        const itemId = element.id.replace('CartItem-', '');
        
        // Get variant ID from quantity input
        const variantId = qtyInput ? qtyInput.getAttribute('data-quantity-variant-id') : null;
        
        // Get product title
        const title = linkElement.textContent.trim();
        
        // Get price
        const priceElement = element.querySelector('.cart-item__final-price, .product-option');
        const price = priceElement ? priceElement.textContent.trim() : '';
        
        // Check if product has universal tag
        const isUniversal = this.checkUniversalTag(element);
        
        return {
          handle,
          itemId,
          variantId,
          title,
          quantity,
          price,
          element,
          isUniversal
        };
      } catch (error) {
        console.error('Error extracting item data:', error);
        return null;
      }
    }

    /**
     * Extract product handle from URL
     */
    extractHandleFromUrl(url) {
      // URL format: /products/product-handle or /products/product-handle?variant=123
      const match = url.match(/\/products\/([^?\/]+)/);
      return match ? match[1] : null;
    }

    /**
     * Check if product has universal tag
     */
    checkUniversalTag(element) {
      // Check product tags or data attributes
      const dataUniversal = element.getAttribute('data-universal');
      return dataUniversal === 'true';
    }

    /**
     * Group items by product handle
     */
    groupByHandle() {
      this.cartItems.forEach(item => {
        if (!item.handle) return;
        
        if (!this.groupedItems[item.handle]) {
          this.groupedItems[item.handle] = {
            handle: item.handle,
            title: item.title,
            items: [],
            totalQuantity: 0,
            firstElement: item.element,
            isUniversal: item.isUniversal
          };
        }
        
        this.groupedItems[item.handle].items.push(item);
        this.groupedItems[item.handle].totalQuantity += item.quantity;
      });
    }

    /**
     * Display grouped items (update UI to show combined quantities)
     */
    displayGroupedItems() {
      Object.values(this.groupedItems).forEach(group => {
        if (group.items.length > 1) {
          // Multiple items with same handle - show combined quantity
          this.showCombinedQuantity(group);
        }
      });
    }

    /**
     * Show combined quantity for grouped items
     */
    showCombinedQuantity(group) {
      const firstItem = group.firstElement;
      const qtyElement = firstItem.querySelector('.cart-items__heading--quantity');
      
      if (qtyElement) {
        // Add combined quantity indicator
        const indicator = document.createElement('div');
        indicator.className = 'cart-item__combined-qty';
        indicator.innerHTML = `
          <small style="color: rgba(var(--color-foreground), 0.7); font-size: 0.85em;">
            ${group.items.length} variants • Total: ${group.totalQuantity}
          </small>
        `;
        qtyElement.appendChild(indicator);
      }
    }

    /**
     * Verify fitment for all items
     */
    async verifyFitment() {
      // Get vehicle data from cart attributes
      const vehicleData = await this.getVehicleData();
      
      if (!vehicleData || !vehicleData.make) {
        console.log('No vehicle selected - skipping fitment verification');
        return;
      }

      // Verify each grouped item
      Object.values(this.groupedItems).forEach(group => {
        this.addFitmentBadge(group, vehicleData);
      });
    }

    /**
     * Get vehicle data from cart
     */
    async getVehicleData() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        return {
          make: cart.attributes?.Vehicle_Make || '',
          year: cart.attributes?.Vehicle_Year || '',
          model: cart.attributes?.Vehicle_Model || '',
          submodel: cart.attributes?.Vehicle_SubModel || '',
          engine: cart.attributes?.Vehicle_Engine || ''
        };
      } catch (error) {
        console.error('Error fetching vehicle data:', error);
        return null;
      }
    }

    /**
     * Add fitment verification badge to item
     */
    addFitmentBadge(group, vehicleData) {
      const firstElement = group.firstElement;
      const detailsElement = firstElement.querySelector('.cart-item__details');
      
      if (!detailsElement) return;

      // Check if already has badge
      if (detailsElement.querySelector('.fitment-badge')) return;

      // Create badge
      const badge = document.createElement('div');
      badge.className = 'fitment-badge';
      
      // Determine fitment status
      let status, message, icon;
      
      if (group.isUniversal) {
        status = CONFIG.universalClass;
        message = CONFIG.messages.universal;
        icon = '✓';
      } else {
        // Check fitment via VFitz data
        const fits = this.checkFitment(group, vehicleData);
        
        if (fits) {
          status = CONFIG.verifiedClass;
          message = CONFIG.messages.verified;
          icon = '✓';
        } else {
          status = CONFIG.notVerifiedClass;
          message = CONFIG.messages.notVerified;
          icon = '⚠';
        }
      }
      
      badge.className = `fitment-badge ${status}`;
      badge.innerHTML = `
        <span class="fitment-icon">${icon}</span>
        <span class="fitment-text">${message}</span>
      `;
      
      // Insert after title
      const titleElement = detailsElement.querySelector('.cart-item__name');
      if (titleElement) {
        titleElement.insertAdjacentElement('afterend', badge);
      }
    }

    /**
     * Check if product fits vehicle
     * This integrates with VFitz validation
     */
    checkFitment(group, vehicleData) {
      // Check if VFitz validated this product
      if (typeof vfitz_original_cart_items !== 'undefined') {
        const productId = group.items[0].variantId;
        const fitmentData = vfitz_original_cart_items[productId];
        
        if (fitmentData && fitmentData.valid) {
          return true;
        }
      }

      // Check cart attributes for product validation
      // VFitz stores validation in cart attributes
      const cartValidationKey = `Item#${group.items[0].itemId}`;
      
      // This would be populated by VFitz cart validation
      // For now, return true if vehicle data exists
      return vehicleData.make && vehicleData.year && vehicleData.model;
    }
  }

  /**
   * Fitment Badge Styles
   */
  function injectStyles() {
    const style = document.createElement('style');
    style.textContent = `
      /* Fitment Verification Badges */
      .fitment-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.4rem 0.8rem;
        border-radius: 0.3rem;
        font-size: 0.85rem;
        font-weight: 600;
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
      }

      .fitment-badge.fitment-verified {
        background: #d4edda;
        color: #155724;
        border: 1px solid #c3e6cb;
      }

      .fitment-badge.fitment-not-verified {
        background: #fff3cd;
        color: #856404;
        border: 1px solid #ffeaa7;
      }

      .fitment-badge.fitment-universal {
        background: #d1ecf1;
        color: #0c5460;
        border: 1px solid #bee5eb;
      }

      .fitment-icon {
        font-weight: bold;
        font-size: 1rem;
      }

      .fitment-text {
        letter-spacing: 0.02em;
      }

      /* Combined Quantity Indicator */
      .cart-item__combined-qty {
        margin-top: 0.5rem;
        padding: 0.3rem 0.6rem;
        background: rgba(var(--color-foreground), 0.05);
        border-radius: 0.3rem;
        display: inline-block;
      }

      /* Checking state */
      .fitment-badge.checking {
        background: #e9ecef;
        color: #495057;
        border: 1px solid #dee2e6;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Initialize on page load
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }

  function start() {
    // Inject styles
    injectStyles();
    
    // Initialize cart manager
    const manager = new CartManager();
    manager.init();
    
    // Re-run on cart updates
    document.addEventListener('cart:updated', () => {
      manager.init();
    });
  }

  // Start the script
  init();

})();
